<template>
  <b-modal
    id="give-recognition-modal"
    ref="modalRef"
    size="lg"
    centered
    hide-footer
    body-class="p-0"
    @hidden="resetModal"
  >
    <!-- Stacked rather than inline: .modal-title is centred with a hand-tuned
         offset for the close button, which an inline pill would throw off. -->
    <template #modal-title>
      <div class="modal-title-stack">
        <span>{{ modalTitle }}</span>
        <active-company-pill />
      </div>
    </template>

    <!-- Step 1: Select the award -->
    <div v-if="step === 1" class="px-4 py-4 step-content">
      <div class="progress-inline">
        <b-progress :value="displayStep" :max="totalSteps" height="4px" class="progress-inline__bar" />
        <span class="progress-inline__text">{{ displayStep }} of {{ totalSteps }}</span>
      </div>
      <h5 class="mb-4"><strong>Select the award</strong></h5>

      <template v-if="openPrograms.length > 0">
        <div class="section-label">Send free form recognition</div>
        <div
          v-for="program in openPrograms"
          :key="program.recognition_award_program_id"
          class="selectable-row"
          :class="{ selected: selectedProgram?.recognition_award_program_id === program.recognition_award_program_id }"
          role="button"
          @click="selectedProgram = program"
        >
          <div class="selectable-row__left">
            <div class="selectable-row__badge">
              <img
                v-if="program.badge_presigned_url"
                :src="program.badge_presigned_url"
                :alt="program.program_name"
                class="selectable-row__badge-image"
              />
              <font-awesome-icon v-else :icon="['fad', 'award']" class="selectable-row__badge-icon" />
            </div>
            <div class="selectable-row__text">
              <span class="selectable-row__name">{{ program.program_name }}</span>
              <span v-if="program.properties?.short_description" class="selectable-row__subtitle">
                {{ program.properties.short_description }}
              </span>
            </div>
          </div>
          <div class="selectable-row__indicator"></div>
        </div>
      </template>

      <template v-if="companyPrograms.length > 0">
        <div class="section-label" :class="{ 'mt-4': openPrograms.length > 0 }">Give a company award</div>
        <div
          v-for="program in companyPrograms"
          :key="program.recognition_award_program_id"
          class="selectable-row"
          :class="{ selected: selectedProgram?.recognition_award_program_id === program.recognition_award_program_id }"
          role="button"
          @click="selectedProgram = program"
        >
          <div class="selectable-row__left">
            <div class="selectable-row__badge">
              <img
                v-if="program.badge_presigned_url"
                :src="program.badge_presigned_url"
                :alt="program.program_name"
                class="selectable-row__badge-image"
              />
              <font-awesome-icon v-else :icon="['fad', 'award']" class="selectable-row__badge-icon" />
            </div>
            <div class="selectable-row__text">
              <span class="selectable-row__name">{{ program.program_name }}</span>
              <span v-if="program.properties?.short_description" class="selectable-row__subtitle">
                {{ program.properties.short_description }}
              </span>
            </div>
          </div>
          <div class="selectable-row__indicator"></div>
        </div>
      </template>
    </div>

    <!-- Step 2: Select employees (multi) -->
    <div v-else-if="step === 2" class="px-4 py-4 step-content">
      <div class="progress-inline">
        <b-progress :value="displayStep" :max="totalSteps" height="4px" class="progress-inline__bar" />
        <span class="progress-inline__text">{{ displayStep }} of {{ totalSteps }}</span>
      </div>
      <h5 class="mb-4"><strong>Who are you recognising?</strong></h5>

      <div class="section-label">Add people</div>

      <div class="search-container">
        <div class="search-input-wrapper">
          <font-awesome-icon :icon="['fal', 'magnifying-glass']" class="search-icon" />
          <b-form-input
            v-model="searchText"
            type="search"
            placeholder="Find members"
            class="search-input-with-icon"
            @input="onSearchInput"
          />
        </div>

        <div v-if="isSearching" class="search-dropdown">
          <div class="text-center py-3">
            <b-spinner small variant="primary" />
          </div>
        </div>

        <div v-else-if="filteredSearchResults.length > 0" class="search-dropdown">
          <div
            v-for="employee in filteredSearchResults"
            :key="employee.employee_id"
            class="search-dropdown__item"
            :class="{ 'search-dropdown__item--selected': isEmployeeSelected(employee) }"
            role="button"
            @mousedown.prevent="toggleEmployee(employee)"
          >
            <div>
              <div class="search-dropdown__name">{{ employee.full_name }}</div>
              <div class="search-dropdown__subtitle">{{ employee.employee_code }}</div>
            </div>
          </div>
        </div>

        <div v-else-if="searchText.length >= 3 && !isSearching" class="search-dropdown">
          <div class="text-muted py-3 px-3">No employees found</div>
        </div>
      </div>

      <div v-if="selectedEmployees.length > 0" class="selected-tags mt-3">
        <span v-for="emp in selectedEmployees" :key="emp.employee_id" class="selected-tag">
          {{ emp.full_name }}
          <font-awesome-icon :icon="['fal', 'xmark']" class="remove-tag" role="button" @click="removeEmployee(emp)" />
        </span>
      </div>
    </div>

    <!-- Step 3: Message, qualities, and reward -->
    <div v-else class="px-4 py-4 step-content">
      <div class="progress-inline">
        <b-progress :value="displayStep" :max="totalSteps" height="4px" class="progress-inline__bar" />
        <span class="progress-inline__text">{{ displayStep }} of {{ totalSteps }}</span>
      </div>
      <h5 class="mb-4"><strong>What would you like to say?</strong></h5>

      <b-alert v-if="errorMessage" variant="danger" show class="mb-3">
        {{ errorMessage }}
      </b-alert>

      <div class="mb-4">
        <div class="section-label">Add a personal message</div>
        <b-form-textarea
          v-model="recipientMessage"
          placeholder="Write a message to the recipient..."
          rows="3"
          max-rows="6"
        />
      </div>

      <div v-if="availableCompanyValues.length > 0" class="mb-4">
        <div class="section-label">Select qualities</div>
        <div class="value-chips">
          <span
            v-for="value in availableCompanyValues"
            :key="value"
            :class="['value-chip', { 'value-chip--selected': selectedCompanyValues.includes(value) }]"
            role="button"
            @click="toggleCompanyValue(value)"
          >
            {{ value }}
          </span>
        </div>
      </div>

      <div v-if="availableAmounts.length > 0" class="mb-3">
        <b-form-checkbox v-model="includeReward" @change="onRewardToggle">
          <strong>Add a reward</strong>
          <br />
          <small class="text-muted">Select the value they'll receive</small>
        </b-form-checkbox>

        <div v-if="includeReward" class="amount-pills mt-2 ml-4">
          <span
            v-for="amount in availableAmounts"
            :key="amount"
            :class="['amount-pill', { 'amount-pill--selected': selectedAmountMinor === amount }]"
            role="button"
            @click="selectedAmountMinor = amount"
          >
            {{ formatAmount(amount) }}
          </span>
        </div>
      </div>

      <div v-else-if="isAdjustableAmount" class="mb-3">
        <b-form-checkbox v-model="includeReward" @change="onRewardToggle">
          <strong>Add a reward</strong>
          <br />
          <small class="text-muted">
            Enter the value they'll receive<template v-if="maxAwardValueMinor != null">
              (up to {{ formatAmount(maxAwardValueMinor) }})</template
            >
          </small>
        </b-form-checkbox>

        <div v-if="includeReward" class="mt-2 ml-4">
          <b-form-input
            :value="typedAwardAmount"
            type="number"
            min="0"
            step="0.01"
            inputmode="decimal"
            placeholder="0.00"
            @input="onTypedAmountInput"
          />
          <small v-if="adjustableAmountError" class="text-danger">{{ adjustableAmountError }}</small>
        </div>
      </div>

      <div v-if="showCategorySelector" class="mb-3">
        <div class="section-label">Spend category<span v-if="categoryRequired"> *</span></div>
        <div class="value-chips">
          <span
            v-for="category in availableCategories"
            :key="category"
            :class="['value-chip', { 'value-chip--selected': selectedCategory === category }]"
            role="button"
            @click="toggleCategory(category)"
          >
            {{ category }}
          </span>
        </div>
        <small v-if="categoryRequired && !selectedCategory" class="text-danger">A category is required.</small>
      </div>
    </div>

    <!-- Footer -->
    <div class="px-4 py-3 footer-container">
      <div>
        <b-button v-if="step > 1 && !(skipAwardSelection && step === 2)" variant="outline-secondary" @click="goBack"
          >Back</b-button
        >
      </div>
      <div class="d-flex">
        <template v-if="step === 1">
          <b-button variant="outline-primary" class="mr-2" @click="onClose">Cancel</b-button>
          <b-button variant="primary" :disabled="!canGoToStep2" @click="step = 2">Next</b-button>
        </template>
        <template v-else-if="step === 2">
          <b-button variant="outline-primary" class="mr-2" @click="onClose">Cancel</b-button>
          <b-button variant="primary" :disabled="!canGoToStep3" @click="step = 3">
            Add {{ selectedEmployees.length }} {{ selectedEmployees.length === 1 ? 'person' : 'people' }}
          </b-button>
        </template>
        <template v-else>
          <b-button variant="outline-primary" class="mr-2" @click="onClose">Cancel</b-button>
          <b-button variant="primary" :disabled="!canSubmit" @click="onSubmit">
            <b-spinner v-if="isSubmitting" small class="mr-1" />
            {{ submitButtonText }}
          </b-button>
        </template>
      </div>
    </div>
  </b-modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ApiClient from '@/ApiClient';
import State from '@/state/State';
import ActiveCompanyPill from '@/pages/recognition/components/ActiveCompanyPill.vue';
import { RECOGNITION_AWARD_TYPES } from '@/pages/recognition/constants';
import { toCurrency } from '@/utils';
import useToast from '@/composables/useToast';

const { doToast, TOAST_VARIANT } = useToast();

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  initialProgram: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['awarded']);

const modalRef = ref(null);
const step = ref(1);
const selectedProgram = ref(null);
const searchText = ref('');
const searchResults = ref([]);
const selectedEmployees = ref([]);
const isSearching = ref(false);

// Step 3 state
const recipientMessage = ref('');
const selectedCompanyValues = ref([]);
const includeReward = ref(false);
const selectedAmountMinor = ref(null);
const selectedCategory = ref(null);
const typedAwardAmount = ref('');
const isSubmitting = ref(false);
const errorMessage = ref('');

watch(
  () => props.initialProgram,
  newVal => {
    if (newVal) {
      step.value = 2;
      selectedProgram.value = newVal;
    }
  },
  { immediate: true },
);

const skipAwardSelection = computed(() => !!props.initialProgram);
const totalSteps = computed(() => (skipAwardSelection.value ? 2 : 3));
const displayStep = computed(() => (skipAwardSelection.value ? step.value - 1 : step.value));

const modalTitle = computed(() => {
  if (step.value === 1) {
    return 'Give award';
  }
  const name = selectedProgram.value?.program_name || 'award';
  if (step.value === 2) {
    return `Give ${name} award`;
  }
  return `Give ${name} award`;
});

const openPrograms = computed(() =>
  props.programs.filter(p => p.program_type === RECOGNITION_AWARD_TYPES.OPEN && p.is_active),
);

const companyPrograms = computed(() =>
  props.programs.filter(p => p.program_type === RECOGNITION_AWARD_TYPES.PROGRAM && p.is_active),
);

const filteredSearchResults = computed(() => {
  const selectedIds = new Set(selectedEmployees.value.map(e => e.employee_id));
  return searchResults.value.filter(e => !selectedIds.has(e.employee_id));
});

const availableCompanyValues = computed(() => {
  const values = selectedProgram.value?.properties?.company_values;
  if (!values || !values.length) {
    return [];
  }
  return values.map(v => v.name || v);
});

// Open awards are non-monetary, so never offer an amount on one even if a program
// configured before that rule still carries values the API would now reject.
const isMonetaryProgram = computed(() => selectedProgram.value?.program_type !== RECOGNITION_AWARD_TYPES.OPEN);

const availableAmounts = computed(() => {
  const vals = isMonetaryProgram.value ? selectedProgram.value?.properties?.award_value_minor : null;
  if (!vals) {
    return [];
  }
  return Array.isArray(vals) ? vals : [vals];
});

const defaultAmount = computed(() => {
  return selectedProgram.value?.properties?.default_award_value_minor || null;
});

const availableCategories = computed(() => selectedProgram.value?.properties?.categories || []);

const categoryRequired = computed(() => Boolean(selectedProgram.value?.properties?.category_required));

// The spend category is chosen by the finaliser only when a monetary reward is being
// given and the program has categories configured.
const showCategorySelector = computed(() => includeReward.value && availableCategories.value.length > 0);

const maxAwardValueMinor = computed(() =>
  isMonetaryProgram.value ? selectedProgram.value?.properties?.max_award_value_minor ?? null : null,
);

// Adjustable mode: no fixed presets but a monetary reward with a cap (mirrors the
// mobile free-amount mode). The giver types the amount, bounded by the cap.
const isAdjustableAmount = computed(() => availableAmounts.value.length === 0 && maxAwardValueMinor.value != null);

const adjustableAmountError = computed(() => {
  if (!includeReward.value || !isAdjustableAmount.value) {
    return '';
  }
  const minor = selectedAmountMinor.value;
  if (!minor || minor <= 0) {
    return 'Enter an amount greater than 0.';
  }
  if (maxAwardValueMinor.value != null && minor > maxAwardValueMinor.value) {
    return `Amount cannot exceed ${formatAmount(maxAwardValueMinor.value)}.`;
  }
  return '';
});

const submitButtonText = computed(() => 'Send now');

const canGoToStep2 = computed(() => !!selectedProgram.value);
const canGoToStep3 = computed(() => selectedEmployees.value.length > 0);
const canSubmit = computed(() => {
  if (isSubmitting.value) {
    return false;
  }
  if (includeReward.value && !selectedAmountMinor.value) {
    return false;
  }
  if (showCategorySelector.value && categoryRequired.value && !selectedCategory.value) {
    return false;
  }
  if (adjustableAmountError.value) {
    return false;
  }
  return true;
});

let debounceTimeout;
let searchRequestId = 0;

function onSearchInput() {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  if (searchText.value.length < 3) {
    searchResults.value = [];
    isSearching.value = false;
    searchRequestId++;
    return;
  }

  debounceTimeout = setTimeout(async () => {
    const requestId = ++searchRequestId;
    isSearching.value = true;
    try {
      const data = await ApiClient.searchRecognitionEmployees({
        searchText: searchText.value,
        divisionIds: selectedProgram.value?.enabled_divisions || [],
        limit: 10,
      });
      if (requestId === searchRequestId) {
        searchResults.value = data.data;
      }
    } catch {
      if (requestId === searchRequestId) {
        searchResults.value = [];
      }
    } finally {
      if (requestId === searchRequestId) {
        isSearching.value = false;
      }
    }
  }, 250);
}

function isEmployeeSelected(employee) {
  return selectedEmployees.value.some(e => e.employee_id === employee.employee_id);
}

function toggleEmployee(employee) {
  const idx = selectedEmployees.value.findIndex(e => e.employee_id === employee.employee_id);
  if (idx >= 0) {
    selectedEmployees.value.splice(idx, 1);
  } else {
    selectedEmployees.value.push(employee);
    searchText.value = '';
    searchResults.value = [];
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    searchRequestId++;
  }
}

function removeEmployee(employee) {
  selectedEmployees.value = selectedEmployees.value.filter(e => e.employee_id !== employee.employee_id);
}

function toggleCompanyValue(value) {
  const idx = selectedCompanyValues.value.indexOf(value);
  if (idx >= 0) {
    selectedCompanyValues.value.splice(idx, 1);
  } else {
    selectedCompanyValues.value.push(value);
  }
}

function toggleCategory(category) {
  selectedCategory.value = selectedCategory.value === category ? null : category;
}

function onRewardToggle(checked) {
  if (checked && defaultAmount.value) {
    selectedAmountMinor.value = defaultAmount.value;
  } else if (!checked) {
    selectedAmountMinor.value = null;
    selectedCategory.value = null;
    typedAwardAmount.value = '';
  }
}

function onTypedAmountInput(value) {
  typedAwardAmount.value = value;
  const parsed = Number.parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  selectedAmountMinor.value = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : null;
}

function formatAmount(amountMinor) {
  return toCurrency(amountMinor);
}

async function onSubmit() {
  errorMessage.value = '';
  isSubmitting.value = true;

  const programId = selectedProgram.value.recognition_award_program_id;
  const eid = State.state.claims?.eid;
  const payload = {
    recognition_award_program_id: programId,
    nominator_employee_id: eid || undefined,
    nominee_employee_id: selectedEmployees.value.map(e => e.employee_id),
    amount_minor: includeReward.value ? selectedAmountMinor.value : null,
    recipient_message: recipientMessage.value || null,
    company_values: selectedCompanyValues.value.length > 0 ? selectedCompanyValues.value : null,
    category: selectedCategory.value || null,
  };

  try {
    await ApiClient.bulkAwardEmployees(programId, payload);
    const count = selectedEmployees.value.length;
    doToast('Success', `Award sent to ${count} ${count === 1 ? 'person' : 'people'}`, TOAST_VARIANT.SUCCESS);
    emit('awarded');
    modalRef.value?.hide();
  } catch (e) {
    const data = e?.response?.data || e?.variables || {};
    const errorCode = data.error_code;
    if (errorCode === 'INSUFFICIENT_FUNDS') {
      errorMessage.value = 'Insufficient funds. Please add funds in Settings.';
    } else if (errorCode === 'BUDGET_EXCEEDED') {
      errorMessage.value = 'Budget limit exceeded for this period.';
    } else {
      errorMessage.value = data.message || e?.message || 'Failed to send awards. Please try again.';
    }
  } finally {
    isSubmitting.value = false;
  }
}

function goBack() {
  if (step.value === 2) {
    // Clear employee selection when going back to program selection,
    // since a different program may have different division scope.
    selectedEmployees.value = [];
    searchText.value = '';
    searchResults.value = [];
  }
  step.value = step.value - 1;
}

function onClose() {
  modalRef.value?.hide();
}

function resetModal() {
  if (props.initialProgram) {
    step.value = 2;
    selectedProgram.value = props.initialProgram;
  } else {
    step.value = 1;
    selectedProgram.value = null;
  }
  selectedEmployees.value = [];
  searchText.value = '';
  searchResults.value = [];
  isSearching.value = false;
  recipientMessage.value = '';
  selectedCompanyValues.value = [];
  includeReward.value = false;
  selectedAmountMinor.value = null;
  selectedCategory.value = null;
  typedAwardAmount.value = '';
  isSubmitting.value = false;
  errorMessage.value = '';
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
}

// Clear the chosen category if the selected program changes, since each program
// configures its own categories.
watch(selectedProgram, () => {
  selectedCategory.value = null;
});
</script>

<style scoped lang="scss">
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.modal-title-stack {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.progress-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  &__bar {
    flex: 1;
  }

  &__text {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);
    white-space: nowrap;
  }
}

:deep(.progress) {
  background-color: var(--palette-color-core-1-lighten-80);
}

:deep(.progress-bar) {
  background-color: var(--palette-color-brand-primary);
}

.step-content {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.section-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--palette-color-default-lighten-30);
  margin-bottom: 0.75rem;
}

.search-container {
  position: relative;
}

.search-input-wrapper {
  position: relative;
}

.search-dropdown {
  position: absolute;
  z-index: 1000;
  width: 100%;
  background: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 15rem;
  overflow-y: auto;
  margin-top: 0.25rem;

  &__item {
    padding: 0.625rem 0.75rem;
    cursor: pointer;
    transition: background-color 0.1s ease;

    &:hover {
      background-color: var(--palette-color-default-lighten-95);
    }

    &--selected {
      background-color: var(--palette-color-core-1-lighten-90);
    }
  }

  &__name {
    font-weight: 500;
    font-size: 0.875rem;
  }

  &__subtitle {
    font-size: 0.8rem;
    color: var(--palette-color-default-lighten-30);
  }
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--palette-color-default-lighten-30);
  z-index: 1;
  pointer-events: none;
}

.search-input-with-icon {
  padding-left: 2.25rem;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.selected-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: var(--palette-color-default-lighten-95);
  color: var(--palette-color-default);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 1rem;
}

.remove-tag {
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
}

.selectable-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  margin-bottom: 0.5rem;

  &:hover {
    border-color: var(--palette-color-core-1-lighten-30);
  }

  &.selected {
    border-color: var(--palette-color-core-1-lighten-30);
    box-shadow: 0 0 0 2px var(--palette-color-core-1-lighten-50);
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__badge {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: var(--palette-color-core-1-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__badge-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__badge-icon {
    color: var(--palette-color-brand-primary);
    font-size: 1.25rem;
  }

  &__name {
    font-weight: 500;
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__subtitle {
    font-size: 0.75rem;
    color: var(--palette-color-default-lighten-30);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__indicator {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    border: 2px solid var(--palette-color-default-lighten-90);
    background: var(--palette-color-base-white);
    flex-shrink: 0;
  }

  &.selected &__indicator {
    border-color: var(--palette-color-brand-primary);
    background: var(--palette-color-brand-primary);
    box-shadow: inset 0 0 0 4px var(--palette-color-base-white);
  }
}

.value-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.value-chip {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: var(--palette-color-default-lighten-95);
  color: var(--palette-color-default);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--palette-color-core-1-lighten-30);
  }

  &--selected {
    background-color: var(--palette-color-core-1-lighten-80);
    border-color: var(--palette-color-brand-primary);
    color: var(--palette-color-brand-primary);
  }
}

.amount-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.amount-pill {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: var(--palette-color-default-lighten-95);
  color: var(--palette-color-default);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--palette-color-core-1-lighten-30);
  }

  &--selected {
    background-color: var(--palette-color-brand-primary);
    color: var(--palette-color-base-white);
    border-color: var(--palette-color-brand-primary);
  }
}

.footer-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-container :deep(.btn) {
  white-space: nowrap;
  min-width: max-content;
  flex: 0 0 auto;
}
</style>
